import React, { useState } from 'react';
import Add from '@mui/icons-material/Add';
import { AppButton } from '../../../../components/UI';
import { Box, Button, Chip, CircularProgress, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import { Link, useParams, withRouter } from 'react-router-dom';
import { GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarQuickFilter } from '@mui/x-data-grid';
import moment from 'moment';
import Block from '@mui/icons-material/Block';
import Replay from '@mui/icons-material/Replay';
import { getMissionsOfSociete, updateStatusMission } from '../../../../services/MissionService';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { commonGridSx, gridLocaleText } from '../../../../utils/gridStyles';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const statusConfig = {
  'en cours': { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  'terminée': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  'annulée': { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

const Missions = ({ snackbarShowMessage, history }) => {
  const [societe, setSociete] = useState([]);
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [rowCount, setRowCount] = useState(0);
  const [missionsTermines, setMissionsTermines] = useState(false);
  const [reactivating, setReactivating] = useState(null);

  const handleUpdateStatusMission = async (missionId, activate) => {
    setReactivating(missionId);
    try {
      const response = await updateStatusMission(missionId, activate);
      snackbarShowMessage(response.data, 'success', 4000);
      setPaginationModel((m) => ({ ...m, page: 0 }));
      if (activate) setMissionsTermines(false);
    } catch {
      snackbarShowMessage(
        activate ? 'Erreur lors de la réactivation de la mission.' : 'Erreur lors de la désactivation de la mission.',
        'error', 4000
      );
    } finally {
      setReactivating(null);
    }
  };

  React.useEffect(() => {
    let isActive = true;
    (async () => {
      setLoading(true);
      try {
        const response = await getMissionsOfSociete(id, paginationModel.page + 1, missionsTermines ? 'passive' : 'active');
        if (!isActive) return;
        const tab = (response.data.missions || []).map((value) => ({
          uuid: value.uuid,
          consultant: value.consultant,
          contact: { name: value.contact, id: value.contactId },
          createdAt: value.createdAt,
          nom_mission: value.nom_mission,
          statut: value.statut,
          num_mission: value.numMission,
          CV: value.CV,
          selection: value.selection,
          idContact: value.idContact,
          priorite: value.priorite,
          pourvoir: value.pourvoir,
          sourceur: value.sourceur,
        }));
        setSociete(tab);
        setRowCount(response.data.count || 0);
      } catch {
        snackbarShowMessage('Erreur de chargement des missions de la société.', 'error', 4000);
      } finally {
        if (isActive) setLoading(false);
      }
    })();
    return () => { isActive = false; };
  }, [id, paginationModel.page, missionsTermines]);

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mr: 'auto' }}>Missions</Typography>
      <FormControlLabel
        control={<Switch checked={missionsTermines} onChange={(e) => { setMissionsTermines(e.target.checked); setPaginationModel((m) => ({ ...m, page: 0 })); }} size="small"
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#03407b' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#03407b' } }} />}
        label={<Typography sx={{ fontSize: '1rem', color: '#64748b', fontWeight: 500 }}>Missions terminées</Typography>}
        sx={{ m: 0 }}
      />
      <GridToolbarQuickFilter
        sx={{
          '& .MuiInputBase-root': { borderRadius: 2, fontSize: '1rem', background: '#f8fafc', px: 1.5, height: 36 },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
          '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
          '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
        }}
        placeholder="Rechercher..."
        debounceMs={400}
      />
      <GridToolbarColumnsButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarFilterButton sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
      <GridToolbarDensitySelector sx={{ textTransform: 'none', fontSize: '1rem', color: '#64748b', borderRadius: 2 }} />
    </GridToolbarContainer>
  );

  const columns = [
    {
      field: 'num_mission', headerName: 'N°', flex: 70, minWidth: 70,
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${id}/missions/${p.row.uuid}/view` }}
          style={{ textDecoration: 'none', color: '#03407b', fontWeight: 600, fontSize: '1rem' }}>
          {p.value}
        </Link>
      ),
    },
    {
      field: 'nom_mission', headerName: 'MISSION', flex: 250, minWidth: 250,
      renderCell: (p) => (
        <Link to={{ pathname: `/societe/${id}/missions/${p.row.uuid}/view` }}
          style={{ textDecoration: 'none', color: '#0f172a', fontWeight: 600, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.value || '—'}
        </Link>
      ),
    },
    {
      field: 'priorite', headerName: 'PRIORITÉ', flex: 90, minWidth: 90, align: 'center', headerAlign: 'center',
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'statut', headerName: 'STATUT', flex: 110, minWidth: 110,
      renderCell: (p) => {
        const key = (p.value || '').toLowerCase();
        const cfg = statusConfig[key] || { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
        return (
          <Chip label={p.value || '—'} size="small"
            sx={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontWeight: 600, fontSize: '1rem', textTransform: 'uppercase', height: 22 }} />
        );
      },
    },
    {
      field: 'createdAt', headerName: 'CRÉÉE LE', flex: 120, minWidth: 120,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY') : '—'}
        </Typography>
      ),
    },
    {
      field: 'consultant', headerName: 'TALENT MANAGER', flex: 150, minWidth: 150,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'sourceur', headerName: 'SOURCEUR', flex: 130, minWidth: 130,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'pourvoir', headerName: 'À POURVOIR', flex: 120, minWidth: 120,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value ?? '—'}</Typography>,
    },
    {
      field: 'CV', headerName: 'CV EN COURS', flex: 110, minWidth: 110, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Link to={{ pathname: `/candidats/missions/${p.row.uuid}/list`, search: `?quantity=${p.value}&isSelected=true` }}
          style={{ textDecoration: 'none' }}>
          <Chip label={p.value ?? 0} size="small"
            sx={{ background: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.8125rem', cursor: 'pointer', border: '1px solid #bbf7d0' }} />
        </Link>
      ),
    },
    {
      field: 'selection', headerName: 'SÉLECTION', flex: 100, minWidth: 100, align: 'center', headerAlign: 'center',
      renderCell: (p) => (
        <Chip label={p.value ?? 0} size="small"
          sx={{ background: '#ebf4fb', color: '#03407b', fontWeight: 600, fontSize: '0.8125rem' }} />
      ),
    },
    {
      field: 'actions_col', headerName: 'ACTIONS', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        missionsTermines ? (
          <Button variant="contained" size="small" disabled={reactivating === p.row.uuid}
            startIcon={reactivating === p.row.uuid ? <CircularProgress size={14} /> : <Replay sx={{ fontSize: 14 }} />}
            onClick={() => handleUpdateStatusMission(p.row.uuid, true)}
            sx={{ background: '#16a34a', '&:hover': { background: '#15803d' }, textTransform: 'none', fontSize: '0.8125rem', borderRadius: 2, px: 1.5, py: 0.5, whiteSpace: 'nowrap' }}>
            Réactiver
          </Button>
        ) : (
          <Button variant="contained" size="small" disabled={reactivating === p.row.uuid}
            startIcon={reactivating === p.row.uuid ? <CircularProgress size={14} /> : <Block sx={{ fontSize: 14 }} />}
            onClick={() => handleUpdateStatusMission(p.row.uuid, false)}
            sx={{ background: '#dc2626', '&:hover': { background: '#b91c1c' }, textTransform: 'none', fontSize: '0.8125rem', borderRadius: 2, px: 1.5, py: 0.5, whiteSpace: 'nowrap' }}>
            Désactiver
          </Button>
        )
      ),
    },
  ];

  const rows = (societe || []).map((s) => ({ ...s, id: s.uuid }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <AppButton variant="primary" size="small" startIcon={<Add fontSize="small" />}
          onClick={() => history.push(`/societe/${id}/missions/add`)}>
          Ajouter une mission
        </AppButton>
      </Box>
      <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '44vh', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.uuid}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[50, 100]}
          disableRowSelectionOnClick
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={commonGridSx}
        />
      </Box>
    </Box>
  );
};

export default withSnackbar(withRouter(Missions));
